import { Redirect } from 'react-router-dom'
import { useSelector } from 'react-redux'
import React, { useEffect, useRef, useState } from 'react'

import ASRIntroduction from './Components/ASRIntroduction'
import ASRTeacherModal from './Components/ASRTeacherModal'
import ASRTeacherModeView from './Components/ASRTeacherModeView'
import ASRInitialSpeechReminders from './Components/ASRInitialSpeechReminders'
import ASRAfterInitialSpeechTimer from './Components/ASRAfterInitialSpeechTimer'
import { ASR_EVENTS, ASR_STATES, ASR_STATES_PROGRESS, MINIMUM_ACCURACY_TO_PASS } from './StudentASRConstants'

import useStudentPreChecks from '../useStudentPreChecks'
import LoadingSpinner from '../Components/LoadingSpinner/LoadingSpinner';
import { PRE_CHECK_MACHINE_STATES } from '../constants'

import { amiraGestures } from '../../Coach/GesturedSpeech'
import { COPY_STRINGS } from '../../../constants/translatedCopy'
import { selectReaderManager } from '../../Reader/ReaderManagerEvents/ReaderManagerSlice'
import useWindowEvents from '../useWindowEvents'
import { endUniversalSocketSession } from '../../../services/SpeechStream'

export default function StudentASRChecks(props) {

    /**
     * props
     */
    const { history, speechProps, showMicIcon, toggleReaderContainer, readerContainerVisible } = props

    /**
     * states
     */
    const [currentASRState, setASRState] = useState(ASR_STATES.INITIAL)
    const [showTeacherModal, setShowTeacherModal] = useState(false)
    const [teacherMode, setTeacherMode] = useState(false)

    /**
     * Mutable values
     */
    const studentDidTry = useRef(false)
    const teacherTestSuccessful = useRef(false)

    /**
     * redux states
     */
    const readerManagerStates = useSelector(selectReaderManager)

    /**
     * hooks
     */
    useWindowEvents()
    const studentPreCheck = useStudentPreChecks({ itemToCheck: PRE_CHECK_MACHINE_STATES.studentASRChecks })
    const { isCurrent } = studentPreCheck

    const isUrlCurrent = document.location.pathname.includes('/student/pre-checks/asr')
    const hasHighBackgroundNoise = readerManagerStates.contextStates.snrTriggered // determines signal is low, noise is high
    const waitingForStudentSpeech = !studentDidTry.current
    const waitingForPhraseEnd = !readerManagerStates.contextStates.phraseEnded
    const waitingForStoryEnd = !readerManagerStates.contextStates.isCurrentStoryEnded

    /**
     * initialize this check
     */
    useEffect(() => {
        if (isCurrent) {
            startASRCheck()
        }
    }, [])

    /**
     * wait to kick off ASR check stages when reader manager starts the ASR story
     */
    useEffect(()=>{
        // run this for retry flow
        if(readerManagerStates.isCurrentStoryStarted && currentASRState === ASR_STATES.INITIAL){
            setASRState(ASR_STATES.INTRODUCTION_SCREEN_1)
        }
        if(readerManagerStates.contextStates.currentWord){
            studentDidTry.current = true
        }
    },[readerManagerStates])

    /**
     * wait to dispatch finish check when reader manager ends the story
     */
    useEffect(()=>{
        // adding the detection end event made it slower.
        if(readerManagerStates.isCurrentStoryEnded && readerManagerStates.contextStates.detectionEnded){
            if(readerManagerStates.contextStates.currentAccuracy < MINIMUM_ACCURACY_TO_PASS){
                openTeacherModal()
            }else{
                finishASRCheck()
            }
        }
    }, [readerManagerStates.isCurrentStoryEnded, readerManagerStates.contextStates.detectionEnded])

    /**
     * Methods
     */
    const startASRCheck = () => {
        if(!readerContainerVisible) toggleReaderContainer()
        showMicIcon()
        studentPreCheck.startCheck()
        goToNextState()
    }
    const retry = () => {
        // this renders the ReaderManager component that runs the story
        history.push("/student/pre-checks/asr")
        studentPreCheck.retryWith(resetState)
    }
    const finishASRCheck = async () => {
        const playWrapUpSpeech = async () => {
            speechProps.playDance({
                gesture: amiraGestures.ThumbsUp
            })
            if(teacherMode){
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.teacher_mode_asr_success })
                speechProps.abortDance()
                teacherTestSuccessful.current = true
                openTeacherModal()
            }else{
                await speechProps.speakWithPromise({ textToSpeak: COPY_STRINGS.student_checks.finish_asr_check })
                speechProps.abortDance()
                studentPreCheck.finishCheck()
            }
            // Close the socket connection so that the next story will
            // start with a fresh one with updated settings.
            endUniversalSocketSession()
        }
        speechProps.abortSpeech(playWrapUpSpeech)
    }
    const openTeacherModal = () => {
        setShowTeacherModal(true)
        // this unmounts the ReaderManager component that runs the story.
        history.push("/student/pre-checks")
    }
    const resetState = () => {
        setShowTeacherModal(false)
        teacherTestSuccessful.current = false
        studentDidTry.current = false
        setASRState(ASR_STATES.INITIAL)
    }
    const goToNextState = () => {
        const currentASRStateIndex = ASR_STATES_PROGRESS.indexOf(currentASRState)
        if (currentASRStateIndex < ASR_STATES_PROGRESS.length - 1) {
            setASRState(ASR_STATES_PROGRESS[currentASRStateIndex + 1])
        }
    }

    /**
     * Start rendering
     */
    if (!studentPreCheck.isCurrent) return null

    if (!isUrlCurrent && !showTeacherModal) return <Redirect to="/student/pre-checks/asr" />

    if (showTeacherModal){
        return <ASRTeacherModal
            speechProps={speechProps}
            handleRetry={retry}
            studentDidTry={studentDidTry}
            teacherMode={teacherMode}
            setTeacherMode={setTeacherMode}
            successfulTest={teacherTestSuccessful.current}
            hasHighBackgroundNoise={hasHighBackgroundNoise}
            endASRCheck={() => studentPreCheck.finishCheck()}/>
    }

    if (currentASRState === ASR_STATES.INTRODUCTION_SCREEN_1){
        return <ASRIntroduction speechProps={speechProps} goToNextState={goToNextState} teacherMode={teacherMode}/>
    }

    return <div>
        { waitingForStudentSpeech && <ASRInitialSpeechReminders speechProps={speechProps} teacherMode={teacherMode} openTeacherModal={openTeacherModal}/> }
        {!waitingForStudentSpeech && waitingForPhraseEnd && <ASRAfterInitialSpeechTimer teacherMode={teacherMode} speechProps={speechProps} openTeacherModal={openTeacherModal}/> }
        { waitingForPhraseEnd && <ASRTeacherModeView teacherMode={teacherMode}/> }
        <LoadingSpinner show={!waitingForPhraseEnd && waitingForStoryEnd } />
    </div>
}